import { useQuery } from '@tanstack/react-query';

import type { GetGenerateTempTagResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';



/**
 * API function to get temp tag generation data from a purchased vehicle
 */
export const getGenerateTempTag = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetGenerateTempTagResponse> => {
	const response = await apiClient.get<GetGenerateTempTagResponse>(
		ordersEndpoints.generateTempTag(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting temp tag generation data from a purchased vehicle
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetGenerateTempTag = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.generateTempTag.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get temp tag data without an order ID.');
			}
			return getGenerateTempTag(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};
